import React, { useEffect, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformName } from '../config/platforms';

const API_URL = process.env.REACT_APP_API_URL || '';

// Re-checked at most this often while moving between pages. The state only changes when a
// token expires or a renewal runs (every 30 minutes on the server), so polling harder buys
// nothing.
const RECHECK_MS = 5 * 60 * 1000;

interface Attention {
  state: 'expired' | 'expiring' | 'renewal_failed';
  message: string;
}

interface Connection {
  id: string;
  platform: string;
  accountName?: string;
  attention?: Attention | null;
}

/**
 * A banner across every page when a network connection needs someone to act.
 *
 * LinkedIn expired on 2026-10-11 and nothing on screen said so: the only sign was a failed
 * post the next day. The server decides what counts (expired, expiring within a week with no
 * renewal, or renewal failing), so this banner and the alert emails agree.
 *
 * Hidden on Platform Connections itself, which shows the same thing in more detail.
 */
export const ConnectionAttentionBanner: React.FC = () => {
  const { token, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState<Connection[]>([]);
  const [checkedAt, setCheckedAt] = useState(0);

  useEffect(() => {
    if (!token || user?.role === 'demo') return;
    // On Platform Connections a connection may be fixed: clear the throttle so the next page
    // checks again rather than showing a warning for up to five more minutes
    if (location.pathname.startsWith('/connections')) { setCheckedAt(0); return; }
    if (Date.now() - checkedAt < RECHECK_MS) return;
    setCheckedAt(Date.now());

    fetch(`${API_URL}/api/connections`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => (res.ok ? res.json() : []))
      .then((data: Connection[]) => setItems(Array.isArray(data) ? data.filter(c => c.attention) : []))
      .catch(() => { /* a banner is not worth an error of its own */ });
    // checkedAt is deliberately not a dependency: it is the throttle, not a trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.role, location.pathname]);

  if (!items.length || location.pathname.startsWith('/connections')) return null;

  const urgent = items.some(c => c.attention?.state !== 'expiring');
  const names = Array.from(new Set(items.map(c => getPlatformName(c.platform))));

  return (
    <Alert
      severity={urgent ? 'error' : 'warning'}
      sx={{ mb: 3 }}
      action={
        <Button color="inherit" size="small" onClick={() => navigate('/connections')}>
          {urgent ? 'Reconnect' : 'Review'}
        </Button>
      }
    >
      {items.length === 1
        ? items[0].attention!.message
        : `${names.join(', ')} need attention: ${items.map(c => c.attention!.message).join(' ')}`}
    </Alert>
  );
};
